'use client';

import { useState, useEffect } from 'react';

interface BanNoticeProps {
  bannedUntil: string | null;
  bannedReason: string;
  onClose: () => void;
}

export default function BanNotice({ bannedUntil, bannedReason, onClose }: BanNoticeProps) {
  const isPermanent = !bannedUntil;
  const [timeLeft, setTimeLeft] = useState('');

  // Countdown timer for temporary bans
  useEffect(() => {
    if (isPermanent || !bannedUntil) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(bannedUntil).getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeLeft('Ban expired - you can now login');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const parts = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

      setTimeLeft(parts.join(' '));
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [bannedUntil, isPermanent]);

  const banExpiry = bannedUntil ? new Date(bannedUntil) : null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-fadeIn">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[#0f0f0f] border border-white/[0.08] rounded-xl p-8 max-w-md w-full shadow-2xl animate-scaleIn">
        {/* Ban Icon */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-white mb-2">Account Banned</h2>
          
          {/* Ban Type Badge */}
          <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full text-sm font-semibold">
            {isPermanent ? (
              <span className="bg-red-500/20 text-red-400 border border-red-500/30 px-3 py-1 rounded-full">
                Permanent Ban
              </span>
            ) : (
              <span className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-3 py-1 rounded-full">
                Temporary Ban
              </span>
            )}
          </div>
        </div>

        {/* Ban Details */}
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4 mb-6 space-y-4">
          <div>
            <div className="text-xs font-medium text-white/50 mb-1">Reason:</div>
            <p className="text-sm text-white/90 break-words">{bannedReason}</p>
          </div>

          {!isPermanent && banExpiry && (
            <>
              <div className="border-t border-white/[0.06] pt-4">
                <div className="text-xs font-medium text-white/50 mb-1">Ban Expires:</div>
                <p className="text-sm text-white/90">
                  {banExpiry.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>

              {timeLeft && (
                <div className="border-t border-white/[0.06] pt-4">
                  <div className="text-xs font-medium text-white/50 mb-1">Time Remaining:</div>
                  <div className="text-2xl font-bold text-purple-400 font-mono tracking-wider">
                    {timeLeft}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Info Message */}
        <div className="bg-blue-500/10 border-l-4 border-blue-500 p-3 rounded text-sm text-blue-300 mb-6">
          <div className="flex items-start gap-2">
            <span className="text-lg">ℹ️</span>
            <p>
              {isPermanent
                ? 'If you believe this is a mistake, please contact support.'
                : 'You will be able to login again after the ban expires.'}
            </p>
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="w-full py-3 bg-white/[0.08] hover:bg-white/[0.12] text-white rounded-lg font-medium transition-colors duration-200"
        >
          Close
        </button>
      </div>
    </div>
  );
}

